import React from "react";
import EnhancedLatexRenderer from "./EnhancedLatexRenderer";
import { AttributeVisualization } from "./AttributeVisualization";

// ============================================================================
// CONSTANTS
// ============================================================================

const SOURCE_KEYWORDS = [
    "Reference source:",
    "Source Link:",
    "Source Links:",
    "Source links:",
    "Source:",
    "Sources:",
    "資料來源：",
    "來源：",
    "來源:",
    "來源連結：",
];

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Extract action links from text based on source keywords
 * @param {string} text - The message text to parse
 * @returns {Array<{title: string, url: string}>} - Array of extracted links
 */
function extractActionLinks(text) {
    const links = [];

    // Find the source section (bounded to its own block — see
    // findSourceSectionSpan below for why it can't run to the end of the string).
    const sourceContent = findSourceSection(text);
    if (!sourceContent) return links;

    // Strip markdown link syntax [label](url) → keep only the href URL.
    // Handles both [text](url) and [url](url) forms the AI may produce.
    const stripped = sourceContent.replace(/\[([^\]]*)\]\(([^)]+)\)/g, "$2");

    // Split by "https" to get individual URLs
    const urlParts = stripped.split(/(?=https)/);

    urlParts.forEach((part) => {
        const trimmedPart = part.trim();
        if (!trimmedPart.startsWith("https")) return;

        // Stop at any character that can't be part of a URL (markdown/whitespace)
        const rawUrl = trimmedPart.split(/[\s\])[>,"'`]/)[0];
        if (!rawUrl) return;

        const url = rawUrl.replace(
            "storage.cloud.google.com",
            "storage.googleapis.com",
        );

        const title = extractTitleFromUrl(url);
        links.push({ title, url });
    });

    return links;
}

/**
 * Find the { start, end } span of a source section within text: from a
 * source keyword to the next blank line (or end of string), whichever comes
 * first. Returns null if no keyword is found.
 *
 * A message can be several concatenated blocks: the category-filter route
 * sends the webhook's table (ending in its own "來源：" line) and then an
 * analysis paragraph, joined with "\n\n". Bounding the match to the next blank
 * line keeps the source section from swallowing that paragraph — otherwise it
 * is cut from the reply and glued onto the last link as a bogus PDF button.
 */
function findSourceSectionSpan(text, keywords) {
    let bestIndex = -1;
    let bestKeywordLength = 0;
    for (const keyword of keywords) {
        const index = text.indexOf(keyword);
        if (index !== -1 && (bestIndex === -1 || index < bestIndex)) {
            bestIndex = index;
            bestKeywordLength = keyword.length;
        }
    }
    if (bestIndex === -1) return null;

    const contentStart = bestIndex + bestKeywordLength;
    const blankLineMatch = text.slice(contentStart).match(/\n\s*\n/);
    const contentEnd = blankLineMatch
        ? contentStart + blankLineMatch.index
        : text.length;

    return { start: bestIndex, end: contentEnd };
}

function findSourceSection(text) {
    const span = findSourceSectionSpan(text, SOURCE_KEYWORDS);
    return span ? text.slice(span.start, span.end) : null;
}

/**
 * Extract a readable title from a URL
 */
function extractTitleFromUrl(url) {
    // Strip the query string and fragment before looking at the path. GPT cites
    // product pages as `.../tg-asd35ab-en/?utm_source=openai` — a trailing slash
    // plus a query — so taking the last path segment first left every link
    // named "?Utm Source=Openai".
    const withoutQuery = url.split("#")[0].split("?")[0];
    const pathParts = withoutQuery.split("/").filter(Boolean);
    let filename = pathParts[pathParts.length - 1] ?? "";

    // Convert to readable title
    const title = filename
        .replace(/\.pdf$/i, "")
        .replace(/[-_]/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/\b\w/g, (l) => l.toUpperCase());

    return title || "Document";
}

/**
 * Clean message text by removing the source link section.
 * Exported so the avatar's text-to-speech drops exactly the same source URLs
 * this renderer hides (see components/tts/speechText.js).
 *
 * Only the source section itself is cut (keyword to next blank line) — see
 * findSourceSectionSpan above. Cutting to the end of the string would drop
 * the analysis paragraph appended after the table's inline "來源：" line.
 */
export function cleanMessageText(text) {
    const span = findSourceSectionSpan(text, SOURCE_KEYWORDS);
    if (!span) return text.trim();
    return (text.slice(0, span.start) + text.slice(span.end)).trim();
}

/**
 * Parse JSON from text with proper brace matching
 * @returns {{data: object|null, endIndex: number}}
 */
function parseJsonFromText(text) {
    const jsonStart = text.indexOf("{");
    if (jsonStart === -1) return { data: null, endIndex: 0 };

    let braceCount = 0;
    let jsonEnd = -1;

    for (let i = jsonStart; i < text.length; i++) {
        if (text[i] === "{") braceCount++;
        if (text[i] === "}") {
            braceCount--;
            if (braceCount === 0) {
                jsonEnd = i + 1;
                break;
            }
        }
    }

    if (jsonEnd === -1) return { data: null, endIndex: 0 };

    try {
        const jsonStr = text.substring(jsonStart, jsonEnd);
        const data = JSON.parse(jsonStr);
        return { data, endIndex: jsonEnd };
    } catch (e) {
        console.error("Failed to parse JSON:", e);
        return { data: null, endIndex: 0 };
    }
}

/**
 * Extract table content from text after "table:" keyword
 * @returns {{tableContent: string, afterTable: string}}
 */
function extractTableContent(textAfterKeyword) {
    // Try to find table end with double newline
    const tableEndMatch = textAfterKeyword.match(/\n\n/);

    if (tableEndMatch) {
        return {
            tableContent: textAfterKeyword
                .substring(0, tableEndMatch.index)
                .trim(),
            afterTable: textAfterKeyword.substring(tableEndMatch.index).trim(),
        };
    }

    // Find last line with pipe character (table row)
    const lines = textAfterKeyword.trim().split("\n");
    let lastTableLineIndex = -1;

    for (let i = lines.length - 1; i >= 0; i--) {
        if (lines[i].includes("|")) {
            lastTableLineIndex = i;
            break;
        }
    }

    if (lastTableLineIndex !== -1) {
        return {
            tableContent: lines
                .slice(0, lastTableLineIndex + 1)
                .join("\n")
                .trim(),
            afterTable: lines
                .slice(lastTableLineIndex + 1)
                .join("\n")
                .trim(),
        };
    }

    return {
        tableContent: textAfterKeyword.trim(),
        afterTable: "",
    };
}

// ============================================================================
// CONTENT PARSER - Converts text to structured content blocks
// ============================================================================

/**
 * Content block types
 */
export const ContentType = {
    TEXT: "text",
    TABLE: "table",
    VIZ: "viz",
};

/**
 * Parse message text into structured content blocks
 * Exported so text-to-speech can describe a table or chart out loud instead of
 * reading its raw markup (see components/tts/speechText.js).
 * @param {string} text - The cleaned message text
 * @returns {Array<{type: string, content: any}>}
 */
export function parseContentBlocks(text) {
    const blocks = [];
    let remaining = text;

    while (remaining.length > 0) {
        const vizIndex = remaining.toLowerCase().indexOf("viz:");
        const tableIndex = remaining.toLowerCase().indexOf("table:");

        // No special content found - add remaining as text
        if (vizIndex === -1 && tableIndex === -1) {
            if (remaining.trim()) {
                blocks.push({
                    type: ContentType.TEXT,
                    content: remaining.trim(),
                });
            }
            break;
        }

        // Determine which comes first
        const nextSpecialIndex = getFirstValidIndex(vizIndex, tableIndex);
        const isVizNext = nextSpecialIndex === vizIndex && vizIndex !== -1;

        // Add text before the special content
        const beforeText = remaining.substring(0, nextSpecialIndex).trim();
        if (beforeText) {
            blocks.push({ type: ContentType.TEXT, content: beforeText });
        }

        // Parse the special content
        if (isVizNext) {
            const afterKeyword = remaining.substring(vizIndex + 4);
            const { data, endIndex } = parseJsonFromText(afterKeyword);

            if (data) {
                blocks.push({ type: ContentType.VIZ, content: data });
                remaining = afterKeyword.substring(endIndex).trim();
            } else {
                // Failed to parse viz, treat as text and continue
                remaining = afterKeyword.trim();
            }
        } else {
            const afterKeyword = remaining.substring(tableIndex + 6);
            const { tableContent, afterTable } =
                extractTableContent(afterKeyword);

            if (tableContent) {
                blocks.push({ type: ContentType.TABLE, content: tableContent });
            }
            remaining = afterTable;
        }
    }

    return blocks;
}

/**
 * Get the first valid (non-negative) index, or the smaller of two valid indices
 */
function getFirstValidIndex(a, b) {
    if (a === -1) return b;
    if (b === -1) return a;
    return Math.min(a, b);
}

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Returns true when the viewport is >= 768px (Tailwind's "md" breakpoint).
 * Updates reactively on window resize.
 */
function useIsDesktop() {
    const [isDesktop, setIsDesktop] = React.useState(
        () => window.matchMedia("(min-width: 768px)").matches,
    );

    React.useEffect(() => {
        const mq = window.matchMedia("(min-width: 768px)");
        const handler = (e) => setIsDesktop(e.matches);
        mq.addEventListener("change", handler);
        return () => mq.removeEventListener("change", handler);
    }, []);

    return isDesktop;
}

// ============================================================================
// PRESENTATIONAL COMPONENTS
// ============================================================================

/**
 * Renders inline text with bold formatting and LaTeX support
 */
function FormattedText({ text }) {
    const boldRegex = /\*\*([^*]+)\*\*/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = boldRegex.exec(text)) !== null) {
        // Add text before the match
        if (match.index > lastIndex) {
            const beforeText = text.slice(lastIndex, match.index);
            parts.push(
                <EnhancedLatexRenderer
                    key={`text-${lastIndex}`}
                    text={beforeText}
                />,
            );
        }

        // Add bold text
        parts.push(
            <strong
                key={`bold-${match.index}`}
                className="font-semibold text-orange-600"
            >
                <EnhancedLatexRenderer text={match[1]} />
            </strong>,
        );

        lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
        parts.push(
            <EnhancedLatexRenderer
                key={`text-${lastIndex}`}
                text={text.slice(lastIndex)}
            />,
        );
    }

    // No formatting found
    if (parts.length === 0) {
        return <EnhancedLatexRenderer text={text} />;
    }

    return <>{parts}</>;
}

/**
 * Renders markdown-style content (bullet lists, paragraphs)
 */
function MarkdownRenderer({ text }) {
    const lines = text.split("\n");
    const elements = [];
    let currentList = [];

    const flushList = () => {
        if (currentList.length === 0) return;

        elements.push(
            <ul
                key={`list-${elements.length}`}
                className="list-disc list-inside my-2 space-y-1"
            >
                {currentList.map((item, idx) => (
                    <li key={idx} className="ml-4">
                        <FormattedText text={item} />
                    </li>
                ))}
            </ul>,
        );
        currentList = [];
    };

    lines.forEach((line) => {
        // Check for bullet point (starts with * followed by space)
        if (/^\s*\*\s+/.test(line)) {
            const content = line.replace(/^\s*\*\s+/, "").trim();
            currentList.push(content);
            return;
        }

        // Not a list item - flush pending list
        flushList();

        // Add regular text or spacing
        if (line.trim()) {
            elements.push(
                <div key={`text-${elements.length}`} className="mb-1">
                    <FormattedText text={line} />
                </div>,
            );
        } else if (elements.length > 0) {
            elements.push(
                <div key={`space-${elements.length}`} className="h-2" />,
            );
        }
    });

    // Flush remaining list items
    flushList();

    return <div>{elements}</div>;
}

/**
 * Renders a markdown table with styling
 */
export function TableRenderer({ tableContent }) {
    const lines = tableContent.trim().split("\n");
    if (lines.length < 2) return null;

    // Parse headers
    const headers = parseTableRow(lines[0]);
    if (headers.length === 0) return null;

    // Check for separator line
    const hasSeparator = lines[1] && /^[\s|:-]+$/.test(lines[1].trim());
    const rowStartIndex = hasSeparator ? 2 : 1;

    // Parse data rows
    const rows = lines
        .slice(rowStartIndex)
        .map(parseTableRow)
        .filter((row) => row.length > 0);

    return (
        <div className="my-4 w-[100%] mx-auto overflow-x-auto">
            <table className="w-full border-collapse border border-orange-500/30 rounded-lg overflow-hidden">
                <thead>
                    <tr className="bg-orange-500/20">
                        {headers.map((header, idx) => (
                            <th
                                key={idx}
                                className="border border-orange-500/30 px-4 py-2 text-left text-black font-semibold"
                                style={getColumnStyle(idx, headers.length)}
                            >
                                {header}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, rowIdx) => (
                        <tr
                            key={rowIdx}
                            className={
                                rowIdx % 2 === 0 ? "bg-white/5" : "bg-white/10"
                            }
                        >
                            {row.map((cell, cellIdx) => (
                                <td
                                    key={cellIdx}
                                    className="border border-orange-500/20 px-4 py-2 text-black"
                                    style={getColumnStyle(cellIdx, row.length)}
                                >
                                    {cell}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/**
 * Parse a table row into cells
 */
export function parseTableRow(line) {
    return line
        .split("|")
        .map((cell) => cell.trim())
        .filter((cell) => cell);
}

/**
 * Get column styling based on index and total columns
 */
function getColumnStyle(index, totalColumns) {
    return {
        wordBreak: "break-word",
        overflowWrap: "break-word",
        whiteSpace: "normal",
        width: index === 0 ? "20%" : `${80 / (totalColumns - 1)}%`,
    };
}

// ============================================================================
// PDF PANEL CONTEXT — shared state for the side-panel PDF viewer
// ============================================================================

export const PdfPanelContext = React.createContext(null);

/**
 * Wrap the page with this provider so all ActionLinks share one PDF side-panel.
 */
export function PdfPanelProvider({ children }) {
    const [pdfs, setPdfs] = React.useState([]);
    const [activeUrl, setActiveUrl] = React.useState(null);

    // Replace all displayed PDFs with a new set from one AI message
    const setAll = React.useCallback((links, clickedUrl) => {
        setPdfs(links);
        setActiveUrl(clickedUrl ?? links[0]?.url ?? null);
    }, []);

    const setActive = React.useCallback((url) => setActiveUrl(url), []);

    const closeAll = React.useCallback(() => {
        setPdfs([]);
        setActiveUrl(null);
    }, []);

    return (
        <PdfPanelContext.Provider
            value={{ pdfs, activeUrl, setAll, setActive, closeAll }}
        >
            {children}
        </PdfPanelContext.Provider>
    );
}

/**
 * Right-side PDF panel with tab bar — one iframe shown at a time.
 * Tabs reset whenever the AI sends a new message with different PDF sources.
 */
export function PdfSidePanel() {
    const ctx = React.useContext(PdfPanelContext);
    if (!ctx || ctx.pdfs.length === 0) return null;

    const { pdfs, activeUrl, setActive, closeAll } = ctx;
    const activePdf = pdfs.find((p) => p.url === activeUrl) ?? pdfs[0];

    return (
        <div className="flex flex-col h-full w-full overflow-hidden">
            {/* Header */}
            <div
                className="flex items-center justify-between px-3 py-2 border-b border-gray-200 shrink-0"
                style={{ backgroundColor: "#E78A24" }}
            >
                <span className="text-xs font-semibold text-white uppercase tracking-wide">
                    Documents
                </span>
                <button
                    onClick={closeAll}
                    className="p-1 text-white hover:scale-110 rounded-lg transition-transform"
                    title="Close"
                >
                    <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>
            </div>

            {/* Tab bar */}
            <div className="flex border-b border-gray-200 overflow-x-auto shrink-0 bg-white">
                {pdfs.map((pdf) => {
                    const label = pdf.title;

                    const isActive = pdf.url === (activeUrl ?? pdfs[0]?.url);
                    return (
                        <button
                            key={pdf.url}
                            onClick={() => setActive(pdf.url)}
                            title={pdf.title}
                            className={`px-3 py-2 text-xs font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
                                isActive
                                    ? "border-orange-500 text-orange-600 bg-orange-50"
                                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                            }`}
                        >
                            {label}
                        </button>
                    );
                })}
            </div>

            {/* Active PDF filename + open-in-new-tab */}
            {activePdf && (
                <div className="flex items-center justify-end px-3 py-1.5 bg-gray-50 border-b border-gray-100 shrink-0">
                    {/* <span className="text-xs text-gray-500 truncate max-w-[70%]">
                        {activePdf.title || "Document"}
                    </span> */}
                    <a
                        href={activePdf.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs text-orange-500 hover:text-orange-700 transition-colors"
                    >
                        New tab
                        <svg
                            className="w-3 h-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                            />
                        </svg>
                    </a>
                </div>
            )}

            {/* iframe */}
            <div className="flex-1 relative min-h-0">
                {activePdf && (
                    <>
                        <iframe
                            key={activePdf.url}
                            src={`${activePdf.url}#toolbar=1&navpanes=0`}
                            title={activePdf.title || "Document"}
                            className="absolute inset-0 w-full h-full border-0"
                        />
                        <div className="absolute inset-0 flex items-end justify-center pb-4 pointer-events-none select-none">
                            <span className="text-[11px] text-gray-400 opacity-40">
                                If PDF does not load, use "New tab"
                            </span>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

/**
 * Renders action links (source documents).
 * - With PdfPanelProvider (expanded view): toggles the shared side panel.
 * - Without provider (small widget): opens each link in a new tab.
 */
function ActionLinks({ links }) {
    const ctx = React.useContext(PdfPanelContext);
    const isDesktop = useIsDesktop();

    if (!links || links.length === 0) return null;

    // No provider → small widget mode: always open in new tab
    if (!ctx) {
        return (
            <div className="mt-3 py-3 border-t-2 border-orange-500">
                <div className="text-sm text-gray-500 pb-1">
                    more info:
                </div>
                <div className="flex flex-row gap-2 flex-wrap">
                    {links.map((link, index) => (
                        <a
                            key={index}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border transition-all duration-200 hover:scale-105 text-sm font-medium bg-orange-500/90 text-white border-orange-500/30"
                        >
                            <DownloadIcon />
                            {link.title || "View Document"}
                            <svg
                                className="w-3.5 h-3.5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                />
                            </svg>
                        </a>
                    ))}
                </div>
            </div>
        );
    }

    // With provider → expanded view: replace all tabs with this message's PDFs
    const { pdfs, activeUrl, setAll } = ctx;

    return (
        <div className="mt-3 py-3 border-t-2 border-orange-500">
            <div className="text-sm text-gray-500 pb-1">
                more info:
            </div>
            <div className="flex flex-row gap-2 flex-wrap">
                {links.map((link, index) => (
                    <ActionLinkButton
                        key={index}
                        link={link}
                        isActive={pdfs === links && activeUrl === link.url}
                        onToggle={() => setAll(links, link.url)}
                        isDesktop={isDesktop}
                    />
                ))}
            </div>
        </div>
    );
}

/**
 * Single action link button — in expanded mode (provider present) selects the PDF in the side panel;
 * on mobile opens the URL directly in a new tab.
 */
function ActionLinkButton({ link, isActive, onToggle, isDesktop }) {
    const baseClass =
        "inline-flex items-center gap-2 px-2 py-2 rounded-lg border transition-all duration-200 hover:scale-105 text-sm font-medium";

    if (!isDesktop) {
        // Mobile: plain link → new tab
        return (
            <a
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`${baseClass} bg-orange-500/90 text-white border-orange-500/30`}
            >
                <DownloadIcon />
                {link.title || "View Document"}
                <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                </svg>
            </a>
        );
    }

    // Desktop with provider: open in side panel tab
    return (
        <button
            type="button"
            onClick={onToggle}
            className={`${baseClass} ${
                isActive
                    ? "bg-orange-600 text-white border-orange-600 shadow-md shadow-orange-500/30"
                    : "bg-orange-500/90 text-white border-orange-500/30 hover:text-orange-200"
            }`}
        >
            <DownloadIcon />
            {link.title || "View Document"}
            <svg
                className={`w-3.5 h-3.5 transition-transform duration-200 ${isActive ? "rotate-180" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                />
            </svg>
        </button>
    );
}

/**
 * Download icon SVG
 */
function DownloadIcon() {
    return (
        <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
        </svg>
    );
}

// ============================================================================
// CONTENT BLOCK RENDERER
// ============================================================================

/**
 * Renders a single content block based on its type
 */
function ContentBlockRenderer({ block, language }) {
    switch (block.type) {
        case ContentType.TEXT:
            return (
                <div className="mb-2">
                    <MarkdownRenderer text={block.content} />
                </div>
            );

        case ContentType.TABLE:
            return <TableRenderer tableContent={block.content} />;

        case ContentType.VIZ:
            return (
                <AttributeVisualization
                    vizData={block.content}
                    language={language}
                    className="overflow-x-auto"
                />
            );

        default:
            return null;
    }
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * Main message renderer component
 * Parses and renders message text with support for:
 * - Markdown formatting (bold, lists)
 * - Tables (table: keyword)
 * - Visualizations (viz: keyword)
 * - Action links (source documents)
 *
 * @param {string} text - The message text to render
 * @param {Array} actionLinks - Optional pre-extracted action links
 * @param {string} language - Language for visualization component
 */
export function MessageRenderer({
    text,
    actionLinks: providedActionLinks,
    language = "en",
}) {
    // Step 1: Clean text and extract links
    const cleanedText = cleanMessageText(text);
    const actionLinks = providedActionLinks || extractActionLinks(text);

    // Step 2: Parse text into content blocks
    const contentBlocks = parseContentBlocks(cleanedText);

    // Step 3: Render all blocks
    return (
        <div className="flex flex-col">
            {contentBlocks.map((block, index) => (
                <ContentBlockRenderer
                    key={`block-${index}`}
                    block={block}
                    language={language}
                />
            ))}
            <ActionLinks links={actionLinks} />
        </div>
    );
}
